import BN from 'bignumber.js'
import { parseNumberToFraction, formatFraction } from '@donaswap/utils/formatFractions'
import { BigintIsh, ZERO } from '@donaswap/sdk'

type BigNumberish = BN | number | string

interface FarmAprParams {
  poolWeight: BigNumberish
  // Total tvl staked in farm in usd
  tvlUsd: BigNumberish
  flamePriceUsd: BigNumberish
  flamePerSecond: BigNumberish

  precision?: number
}

const SECONDS_FOR_YEAR = 365 * 60 * 60 * 24

const isValid = (num: BigNumberish) => {
  const bigNumber = new BN(num)
  return bigNumber.isFinite() && bigNumber.isPositive()
}

const formatNumber = (bn: BN, precision: number) => {
  return formatFraction(parseNumberToFraction(bn.toNumber(), precision), precision)
}

export function getFarmApr({ poolWeight, tvlUsd, flamePriceUsd, flamePerSecond, precision = 6 }: FarmAprParams) {
  if (!isValid(poolWeight) || !isValid(tvlUsd) || !isValid(flamePriceUsd) || !isValid(flamePerSecond)) {
    return '0'
  }

  const flameRewardPerYear = new BN(flamePerSecond).times(SECONDS_FOR_YEAR)
  const farmApr = new BN(poolWeight).times(flameRewardPerYear).times(flamePriceUsd).div(tvlUsd).times(100)

  if (farmApr.isZero()) {
    return '0'
  }

  return formatNumber(farmApr, precision)
}

export interface PositionFarmAprParams extends Omit<FarmAprParams, 'tvlUsd'> {
  // Position liquidity
  liquidity: BigintIsh

  // Total staked liquidity in farm
  totalStakedLiquidity: BigintIsh

  // Position tvl in usd
  positionTvlUsd: BigNumberish
}

export function getPositionFarmApr({
  poolWeight,
  positionTvlUsd,
  flamePriceUsd,
  flamePerSecond,
  liquidity,
  totalStakedLiquidity,
  precision = 6,
}: PositionFarmAprParams) {
  const aprFactor = getPositionFarmAprFactor({
    poolWeight,
    flamePriceUsd,
    flamePerSecond,
    liquidity,
    totalStakedLiquidity,
  })
  if (!isValid(aprFactor) || !isValid(positionTvlUsd)) {
    return '0'
  }

  const positionApr = aprFactor.times(liquidity.toString()).div(positionTvlUsd)

  return formatNumber(positionApr, precision)
}

export function getPositionFarmAprFactor({
  poolWeight,
  flamePriceUsd,
  flamePerSecond,
  liquidity,
  totalStakedLiquidity,
}: Omit<PositionFarmAprParams, 'positionTvlUsd' | 'precision'>) {
  if (
    !isValid(poolWeight) ||
    !isValid(flamePriceUsd) ||
    !isValid(flamePerSecond) ||
    BigInt(liquidity) === ZERO ||
    BigInt(totalStakedLiquidity) === ZERO
  ) {
    return new BN(0)
  }

  const flameRewardPerYear = new BN(flamePerSecond).times(SECONDS_FOR_YEAR)
  const aprFactor = new BN(poolWeight)
    .times(flameRewardPerYear)
    .times(flamePriceUsd)
    .div((BigInt(liquidity) + BigInt(totalStakedLiquidity)).toString())
    .times(100)

  return aprFactor
}
